import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, RefreshCw, Users, Download, Upload, ChevronRight, Eye, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import procesoService from '../../services/procesoService';
import perfilService from '../../services/perfilService';
import usuarioService from '../../services/usuarioService';
import useAuthStore from '../../store/authStore';
import { descargarPlantillaCrearUsuarios, descargarPlantillaActualizarUsuarios, leerArchivoExcel, leerArchivoExcelConCorreo, validarArchivoExcel } from '../../utils/excelHelper';

// Colores corporativos
const COLORES = {
  naranja: '#fe7f2d',
  verde: '#297373'
};

export default function Procesos() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [procesos, setProcesos] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPerfilesModal, setShowPerfilesModal] = useState(false);
  const [showUsuariosModal, setShowUsuariosModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedProceso, setSelectedProceso] = useState(null);

  useEffect(() => {
    loadProcesos();
  }, []);

  const loadProcesos = async () => {
    try {
      setLoading(true);
      const [procesosData, perfilesData, usuariosData] = await Promise.all([
        procesoService.getAllProcesos().catch(err => {
          console.error('Error al cargar procesos:', err);
          return [];
        }),
        perfilService.getAllPerfiles().catch(err => {
          console.error('Error al cargar perfiles:', err);
          return [];
        }),
        usuarioService.getAllUsuarios().catch(err => {
          console.error('Error al cargar usuarios:', err);
          return [];
        })
      ]);

      // Enriquecer cada proceso con el conteo real de usuarios desde usuario-proceso
      const procesosEnriquecidos = await Promise.all(
        (procesosData || []).map(async (proceso) => {
          try {
            const usuariosProceso = await procesoService.getUsuariosPorProceso(proceso.id);
            return {
              ...proceso,
              usuariosAsignados: usuariosProceso || [],
              countUsuarios: usuariosProceso?.length || 0
            };
          } catch (error) {
            console.error(`Error al cargar usuarios del proceso ${proceso.id}:`, error);
            return {
              ...proceso,
              usuariosAsignados: [],
              countUsuarios: 0
            };
          }
        })
      );

      setProcesos(procesosEnriquecidos || []);
      setPerfiles(perfilesData || []);
      setUsuarios(usuariosData || []);
    } catch (error) {
      console.error('Error general al cargar datos:', error);
      setProcesos([]);
      setPerfiles([]);
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await procesoService.updateProceso(editingItem.id, data);
        toast.success('Proceso actualizado');
      } else {
        await procesoService.createProceso({ ...data, usuarioCreador: { id: user.id } });
        toast.success('Proceso creado');
      }
      loadProcesos();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este proceso?')) return;

    try {
      await procesoService.deleteProceso(id);
      toast.success('Proceso eliminado');
      loadProcesos();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const openPerfilesModal = (proceso) => {
    setSelectedProceso(proceso);
    setShowPerfilesModal(true);
  };

  const openUsuariosModal = (proceso) => {
    setSelectedProceso(proceso);
    setShowUsuariosModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const closePerfilesModal = () => {
    setShowPerfilesModal(false);
    setSelectedProceso(null);
  };

  const closeUsuariosModal = () => {
    setShowUsuariosModal(false);
    setSelectedProceso(null);
  };

  const handleSavePerfiles = async (selectedPerfilIds) => {
    try {
      const perfilesToAssign = selectedPerfilIds.map(id => ({ id }));
      await procesoService.updateProceso(selectedProceso.id, {
        nombre: selectedProceso.nombre,
        descripcion: selectedProceso.descripcion,
        perfiles: perfilesToAssign
      });
      toast.success('Perfiles asignados correctamente');
      loadProcesos();
      closePerfilesModal();
    } catch (error) {
      toast.error('Error al asignar perfiles');
      console.error(error);
    }
  };

  const handleSaveUsuarios = async (selectedUsuarioIds) => {
    try {
      await procesoService.asignarUsuariosAProceso(selectedProceso.id, selectedUsuarioIds);
      toast.success('Usuarios asignados correctamente');
      loadProcesos();
      closeUsuariosModal();
    } catch (error) {
      toast.error('Error al asignar usuarios');
      console.error(error);
    }
  };

  const handleAsignarUsuario = async (usuarioId) => {
    try {
      await procesoService.asignarUsuarioAProceso(selectedProceso.id, usuarioId);
      toast.success('Usuario asignado correctamente');
      loadProcesos();
      // No cerramos el modal para permitir asignar más usuarios
    } catch (error) {
      toast.error('Error al asignar usuario');
      console.error(error);
    }
  };

  const filteredProcesos = procesos.filter(p => {
    const nombre = (p.nombre || '').toLowerCase();
    const descripcion = (p.descripcion || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return nombre.includes(term) || descripcion.includes(term);
  });

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredProcesos.length / itemsPerPage);
  const paginatedProcesos = filteredProcesos.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Resetear página cuando cambia la búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Procesos</h1>
          <p className="text-neutral-600 mt-1">Gestión de procesos de selección</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Proceso
        </Button>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar procesos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadProcesos} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Vista de Tabla (Desktop) */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-neutral-200 overflow-x-auto">
        <table className="w-full min-w-full">
          <thead className="border-b border-neutral-200" style={{ background: `linear-gradient(90deg, ${COLORES.verde}10, ${COLORES.naranja}10)` }}>
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Nombre</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Descripción</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Perfiles</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Usuarios</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Creador</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Fecha Inicio</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Fecha Fin</th>
              <th className="text-center p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {paginatedProcesos.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-neutral-500">
                  No se encontraron procesos
                </td>
              </tr>
            ) : (
              paginatedProcesos.map((proceso) => (
                <tr
                  key={proceso.id}
                  className="hover:bg-neutral-50 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/admin/procesos/${proceso.id}`)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-10 rounded-full"
                        style={{ backgroundColor: COLORES.verde }}
                      />
                      <span className="font-semibold text-neutral-800 group-hover:text-[#297373] transition-colors">
                        {proceso.nombre}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-neutral-600 max-w-xs truncate">{proceso.descripcion}</td>
                  <td className="p-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${Array.isArray(proceso.perfiles) && proceso.perfiles.length > 0
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-100 text-gray-800'
                      }`}>
                      {Array.isArray(proceso.perfiles) ? proceso.perfiles.length : 0}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${proceso.countUsuarios > 0
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-800'
                        }`}
                      style={proceso.countUsuarios > 0 ? { backgroundColor: COLORES.naranja } : {}}
                    >
                      {proceso.countUsuarios || 0}
                    </span>
                  </td>
                  <td className="p-4 text-neutral-600 text-sm">{proceso.usuarioCreadorNombre || (proceso.usuarioCreador ? `${proceso.usuarioCreador.nombre} ${proceso.usuarioCreador.apellido}` : 'N/A')}</td>
                  <td className="p-4 text-neutral-600 text-sm">
                    {proceso.fechaCreacion ? new Date(proceso.fechaCreacion).toLocaleDateString('es-PE') : 'N/A'}
                  </td>
                  <td className="p-4 text-neutral-600 text-sm">
                    {proceso.fechaFinalizacion ? (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle className="w-4 h-4" />
                        {new Date(proceso.fechaFinalizacion).toLocaleDateString('es-PE')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        En Curso
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/procesos/${proceso.id}`);
                        }}
                        className="p-2 rounded-md transition-colors"
                        style={{ color: COLORES.verde }}
                        title="Ver Detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPerfilesModal(proceso);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Gestionar Perfiles"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openUsuariosModal(proceso);
                        }}
                        className="p-2 hover:bg-green-50 rounded-md transition-colors"
                        style={{ color: COLORES.verde }}
                        title="Gestionar Usuarios"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(proceso);
                        }}
                        className="p-2 hover:bg-orange-50 rounded-md transition-colors"
                        style={{ color: COLORES.naranja }}
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(proceso.id);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-[#297373] ml-1 transition-colors" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Vista de Tarjetas (Móvil) */}
      <div className="md:hidden space-y-3">
        {paginatedProcesos.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center text-neutral-500">
            No se encontraron procesos
          </div>
        ) : (
          paginatedProcesos.map((proceso) => (
            <div
              key={proceso.id}
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/admin/procesos/${proceso.id}`)}
            >
              {/* Header de la tarjeta */}
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-1.5 h-12 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLORES.verde }}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-neutral-800 truncate">{proceso.nombre}</h3>
                  <p className="text-sm text-neutral-500 truncate">{proceso.descripcion}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-neutral-400 flex-shrink-0" />
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${Array.isArray(proceso.perfiles) && proceso.perfiles.length > 0
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-gray-100 text-gray-800'
                  }`}>
                  {Array.isArray(proceso.perfiles) ? proceso.perfiles.length : 0} Perfiles
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${proceso.countUsuarios > 0
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-800'
                    }`}
                  style={proceso.countUsuarios > 0 ? { backgroundColor: COLORES.naranja } : {}}
                >
                  {proceso.countUsuarios || 0} Usuarios
                </span>
              </div>

              {/* Acciones */}
              <div className="flex items-center justify-between pt-3 border-t border-neutral-100" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-neutral-500">
                    Inicio: {proceso.fechaCreacion ? new Date(proceso.fechaCreacion).toLocaleDateString('es-PE') : 'N/A'}
                  </span>
                  {proceso.fechaFinalizacion ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Fin: {new Date(proceso.fechaFinalizacion).toLocaleDateString('es-PE')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 w-fit">
                      En Curso
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/procesos/${proceso.id}`);
                    }}
                    className="p-2 rounded-md transition-colors"
                    style={{ color: COLORES.verde }}
                    title="Ver Detalle"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openUsuariosModal(proceso);
                    }}
                    className="p-2 hover:bg-green-50 rounded-md transition-colors"
                    style={{ color: COLORES.verde }}
                    title="Gestionar Usuarios"
                  >
                    <Users className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(proceso);
                    }}
                    className="p-2 hover:bg-orange-50 rounded-md transition-colors"
                    style={{ color: COLORES.naranja }}
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(proceso.id);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-neutral-500 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                currentPage === page
                  ? 'text-white border'
                  : 'text-neutral-500 bg-white border border-neutral-300 hover:bg-neutral-50'
              }`}
              style={currentPage === page ? { backgroundColor: COLORES.verde, borderColor: COLORES.verde } : {}}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-neutral-500 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Modal */}
      <ProcesoModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
      />

      {/* Modal de Gestión de Perfiles */}
      <PerfilesModal
        isOpen={showPerfilesModal}
        onClose={closePerfilesModal}
        onSave={handleSavePerfiles}
        proceso={selectedProceso}
        perfiles={perfiles}
      />

      {/* Modal de Gestión de Usuarios */}
      <UsuariosModal
        isOpen={showUsuariosModal}
        onClose={closeUsuariosModal}
        onSave={handleSaveUsuarios}
        proceso={selectedProceso}
        usuarios={usuarios}
      />
    </div>
  );
}

function ProcesoModal({ isOpen, onClose, onSave, initialData }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    fechaFinalizacion: ''
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre || '',
        descripcion: initialData.descripcion || '',
        fechaFinalizacion: initialData.fechaFinalizacion ? initialData.fechaFinalizacion.split('T')[0] : ''
      });
    } else {
      setFormData({ nombre: '', descripcion: '', fechaFinalizacion: '' });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Enviar fechaFinalizacion como null si está vacío, o como ISO string si tiene valor
    const dataToSave = {
      ...formData,
      fechaFinalizacion: formData.fechaFinalizacion ? new Date(formData.fechaFinalizacion + 'T00:00:00').toISOString() : null
    };
    onSave(dataToSave);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Proceso' : 'Nuevo Proceso'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nombre"
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          required
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Descripción
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={4}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Fecha de Finalización (Opcional)
          </label>
          <input
            type="date"
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.fechaFinalizacion}
            onChange={(e) => setFormData({ ...formData, fechaFinalizacion: e.target.value })}
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PerfilesModal({ isOpen, onClose, onSave, proceso, perfiles }) {
  const [selectedPerfiles, setSelectedPerfiles] = useState([]);

  useEffect(() => {
    if (proceso && Array.isArray(proceso.perfiles)) {
      setSelectedPerfiles(proceso.perfiles.map(p => p.id));
    } else {
      setSelectedPerfiles([]);
    }
  }, [proceso, isOpen]);

  const handleTogglePerfil = (perfilId) => {
    setSelectedPerfiles(prev =>
      prev.includes(perfilId)
        ? prev.filter(id => id !== perfilId)
        : [...prev, perfilId]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(selectedPerfiles);
  };

  if (!proceso) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Gestionar Perfiles - ${proceso.nombre}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <p className="text-sm text-neutral-600 mb-4">
            Selecciona los perfiles que deseas asignar a este proceso:
          </p>
          {Array.isArray(proceso.perfiles) && proceso.perfiles.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm font-medium text-blue-800 mb-2">
                Perfiles actualmente asignados ({proceso.perfiles.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {proceso.perfiles.map((perfil) => (
                  <span
                    key={perfil.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {perfil.nombre}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto border border-neutral-200 rounded-md">
            {perfiles.length === 0 ? (
              <p className="p-4 text-center text-neutral-500">
                No hay perfiles disponibles. Crea perfiles primero.
              </p>
            ) : (
              perfiles.map((perfil) => (
                <label
                  key={perfil.id}
                  className="flex items-center p-3 hover:bg-neutral-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedPerfiles.includes(perfil.id)}
                    onChange={() => handleTogglePerfil(perfil.id)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-neutral-800">{perfil.nombre}</div>
                    <div className="text-sm text-neutral-600">{perfil.descripcion}</div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar Cambios
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function UsuariosModal({ isOpen, onClose, onSave, proceso, usuarios }) {
  const { user } = useAuthStore();
  const [usuariosAsignados, setUsuariosAsignados] = useState([]);
  const [todosLosUsuarios, setTodosLosUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importando, setImportando] = useState(false);

  // Función para recargar usuarios asignados
  const recargarUsuariosAsignados = async () => {
    if (!proceso) return;

    try {
      setLoading(true);
      const usuariosProceso = await procesoService.getUsuariosPorProceso(proceso.id);
      setUsuariosAsignados(usuariosProceso.map(up => up.usuarioId));
      console.log('✅ Lista de usuarios asignados actualizada:', usuariosProceso.length);
    } catch (error) {
      console.error('Error al cargar usuarios asignados:', error);
      setUsuariosAsignados([]);
    } finally {
      setLoading(false);
    }
  };

  // Función para recargar todos los usuarios
  const recargarTodosLosUsuarios = async () => {
    try {
      const usuariosData = await usuarioService.getAllUsuarios();
      setTodosLosUsuarios(usuariosData);
      console.log('✅ Lista completa de usuarios actualizada:', usuariosData.length);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  // Cargar usuarios asignados y todos los usuarios cuando se abre el modal
  useEffect(() => {
    if (proceso && isOpen) {
      recargarUsuariosAsignados();
      recargarTodosLosUsuarios();
    }
  }, [proceso, isOpen]);

  const handleDescargarPlantillaCrear = () => {
    descargarPlantillaCrearUsuarios();
    toast.success('Plantilla para crear usuarios descargada');
  };

  const handleDescargarPlantillaActualizar = () => {
    descargarPlantillaActualizarUsuarios();
    toast.success('Plantilla para actualizar usuarios descargada');
  };

  const handleImportarExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!validarArchivoExcel(file)) {
      toast.error('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      event.target.value = '';
      return;
    }

    try {
      setImportando(true);
      toast.loading('Procesando archivo Excel...', { id: 'import' });

      // Leer archivo CON CORREO para crear usuarios
      const usuariosData = await leerArchivoExcelConCorreo(file);

      console.log('📁 Archivo Excel leído:', usuariosData);
      toast.loading(`Creando ${usuariosData.length} usuarios...`, { id: 'import' });

      // Agregar supervisorId a cada usuario
      const usuariosConSupervisor = usuariosData.map(u => ({
        ...u,
        supervisorId: user.id
      }));

      console.log('👤 Usuarios con supervisor:', usuariosConSupervisor);

      // Crear usuarios masivamente
      const resultados = await usuarioService.createUsuariosMasivo(usuariosConSupervisor);

      console.log('📊 Resultados de creación:', resultados);

      // Asignar usuarios exitosos al proceso
      let asignacionesExitosas = 0;
      let asignacionesFallidas = 0;

      if (resultados.exitosos.length > 0) {
        toast.loading(`Asignando ${resultados.exitosos.length} usuarios al proceso ${proceso.id}...`, { id: 'import' });

        console.log(`🚀 Iniciando asignación de ${resultados.exitosos.length} usuarios al proceso ${proceso.id}`);

        for (let i = 0; i < resultados.exitosos.length; i++) {
          const resultado = resultados.exitosos[i];

          try {
            // Verificar que el usuario tenga ID
            if (!resultado.usuario || !resultado.usuario.id) {
              console.error(`❌ Usuario ${i + 1}/${resultados.exitosos.length} sin ID:`, resultado);
              asignacionesFallidas++;
              continue;
            }

            const usuarioId = resultado.usuario.id;
            console.log(`🔄 [${i + 1}/${resultados.exitosos.length}] Asignando usuario ID ${usuarioId} (DNI: ${resultado.datos.dni}) al proceso ${proceso.id}...`);

            // Asignar al proceso con await explícito
            const respuesta = await procesoService.asignarUsuarioAProceso(proceso.id, usuarioId);

            console.log(`✅ [${i + 1}/${resultados.exitosos.length}] Usuario ${usuarioId} asignado exitosamente`, respuesta);
            asignacionesExitosas++;

            // Pequeño delay para evitar saturar el servidor
            await new Promise(resolve => setTimeout(resolve, 100));

          } catch (error) {
            console.error(`❌ [${i + 1}/${resultados.exitosos.length}] Error al asignar usuario ${resultado.usuario?.id}:`, error);
            console.error('📋 Detalles del error:', {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              message: error.message
            });
            asignacionesFallidas++;
          }
        }
      }

      console.log(`📈 Resumen final: ${asignacionesExitosas} asignaciones exitosas, ${asignacionesFallidas} fallidas`);

      // Mostrar resumen
      toast.dismiss('import');

      const mensajeResumen = [];

      if (resultados.exitosos.length > 0) {
        // Separar creados de actualizados
        const creados = resultados.exitosos.length - (resultados.actualizados?.length || 0);
        if (creados > 0) {
          mensajeResumen.push(`✅ ${creados} usuarios creados`);
        }
      }

      if (resultados.actualizados?.length > 0) {
        mensajeResumen.push(`🔄 ${resultados.actualizados.length} usuarios actualizados (ya existían)`);
      }

      if (asignacionesExitosas > 0) {
        mensajeResumen.push(`✅ ${asignacionesExitosas} asignados al proceso`);
      }

      if (resultados.fallidos.length > 0) {
        mensajeResumen.push(`⚠️ ${resultados.fallidos.length} fallidos`);
      }

      if (asignacionesFallidas > 0) {
        mensajeResumen.push(`⚠️ ${asignacionesFallidas} asignaciones fallidas`);
      }

      if (resultados.fallidos.length === 0 && asignacionesFallidas === 0) {
        toast.success(mensajeResumen.join(' | '), { duration: 7000 });
      } else {
        toast(mensajeResumen.join('\n'), {
          icon: '📊',
          duration: 7000
        });

        // Mostrar detalles de errores de creación
        if (resultados.fallidos.length > 0) {
          console.error('❌ Usuarios con creación fallida:', resultados.fallidos);
          resultados.fallidos.forEach((fallo, index) => {
            if (index < 3) { // Mostrar solo los primeros 3 errores
              toast.error(`${fallo.datos.dni}: ${fallo.error}`, { duration: 6000 });
            }
          });
        }
      }

      console.log('🔄 Recargando usuarios asignados...');

      // Recargar los usuarios asignados y la lista completa - esto actualizará el modal
      await Promise.all([
        recargarUsuariosAsignados(),
        recargarTodosLosUsuarios()
      ]);

      toast.success('✅ Proceso completado. Usuarios actualizados en el modal.', { duration: 5000 });

    } catch (error) {
      toast.dismiss('import');
      toast.error(error.message || 'Error al procesar el archivo Excel');
      console.error('❌ Error crítico en importación:', error);
    } finally {
      setImportando(false);
      event.target.value = '';
    }
  };

  const handleAsignarUsuario = async (usuarioId) => {
    try {
      await procesoService.asignarUsuarioAProceso(proceso.id, usuarioId);
      toast.success('Usuario asignado correctamente');
      await recargarUsuariosAsignados();
    } catch (error) {
      toast.error('Error al asignar usuario');
      console.error(error);
    }
  };

  // Función para ACTUALIZAR usuarios existentes (PUT)
  const handleActualizarUsuarios = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validarArchivoExcel(file)) {
      toast.error('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      event.target.value = '';
      return;
    }

    try {
      setImportando(true);
      toast.loading('Procesando archivo Excel...', { id: 'import' });

      const usuariosData = await leerArchivoExcel(file);

      console.log('📁 Archivo Excel leído:', usuariosData);
      toast.loading(`Actualizando ${usuariosData.length} usuarios...`, { id: 'import' });

      // Usar PUT para actualizar usuarios existentes por DNI
      const resultados = await usuarioService.updateUsuariosMasivoPorDni(
        usuariosData,
        proceso.id,
        user.id
      );

      console.log('📊 Resultados de actualización:', resultados);

      toast.dismiss('import');

      const mensajes = [];
      if (resultados.exitosos.length > 0) mensajes.push(`✅ ${resultados.exitosos.length} usuarios actualizados`);
      if (resultados.fallidos.length > 0) mensajes.push(`⚠️ ${resultados.fallidos.length} fallidos`);

      if (resultados.fallidos.length === 0) {
        toast.success(mensajes.join(' | '), { duration: 7000 });
      } else {
        toast(mensajes.join('\n'), { icon: '📊', duration: 7000 });
        resultados.fallidos.slice(0, 3).forEach(fallo => {
          toast.error(`DNI ${fallo.datos.dni}: ${fallo.error}`, { duration: 6000 });
        });
      }

      await Promise.all([recargarUsuariosAsignados(), recargarTodosLosUsuarios()]);

    } catch (error) {
      toast.dismiss('import');
      toast.error(error.message || 'Error al procesar el archivo Excel');
    } finally {
      setImportando(false);
      event.target.value = '';
    }
  };

  if (!proceso) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Asignar Usuarios - ${proceso.nombre}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Dos opciones de importación */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Opción 1: Crear usuarios nuevos */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-green-800 mb-2">
              Asignar Usuarios Nuevos
            </p>
            <p className="text-xs text-green-700 mb-3">
              Para usuarios que <strong>NO existen</strong>. Contraseña: <strong>Igual al DNI</strong>
            </p>
            <div className="space-y-2">
              <Button
                variant="outline"
                onClick={handleDescargarPlantillaCrear}
                className="gap-2 w-full text-green-700 border-green-300 hover:bg-green-100"
                size="sm"
              >
                <Download className="w-4 h-4" />
                Plantilla
              </Button>
              <label className="w-full block">
                <Button
                  variant="primary"
                  as="span"
                  className="gap-2 w-full cursor-pointer bg-green-600 hover:bg-green-700"
                  size="sm"
                  disabled={importando}
                >
                  <Upload className="w-4 h-4" />
                  {importando ? 'Procesando...' : 'Crear'}
                </Button>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportarExcel}
                  className="hidden"
                  disabled={importando}
                />
              </label>
            </div>
          </div>

          {/* Opción 2: Actualizar usuarios existentes */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-semibold text-amber-800 mb-2">
              Asignar Usuarios(ERP)
            </p>
            <p className="text-xs text-amber-700 mb-3">
              Para usuarios que <strong>YA existen</strong>. Se asignarán por DNI. <strong>USAR CUANDO ERP ESTE OPERATIVO</strong>
            </p>
            <div className="space-y-2">
              <Button
                variant="outline"
                onClick={handleDescargarPlantillaActualizar}
                className="gap-2 w-full text-amber-700 border-amber-300 hover:bg-amber-100"
                size="sm"
              >
                <Download className="w-4 h-4" />
                Plantilla
              </Button>
              <label className="w-full block">
                <Button
                  variant="primary"
                  as="span"
                  className="gap-2 w-full cursor-pointer bg-amber-600 hover:bg-amber-700"
                  size="sm"
                  disabled={importando}
                >
                  <Upload className="w-4 h-4" />
                  {importando ? 'Procesando...' : 'Actualizar'}
                </Button>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleActualizarUsuarios}
                  className="hidden"
                  disabled={importando}
                />
              </label>
            </div>
          </div>
        </div>


        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
